import {createContext, type RefObject, useCallback, useMemo, useRef} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {ItemType} from '../../api/memex-items/item-type'
import {TableRowKeyboardUI} from '../../api/stats/contracts'
import {useOpenParentIssue} from '../../features/sub-issues/use-open-parent-issue'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {isMemexItemTypeArchivable} from '../../helpers/archive-util'
import {columnActionable} from '../../helpers/column-actionable'
import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {isPlatformMeta} from '../../helpers/util'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {getColumnMeta, getColumnModel} from '../../hooks/tanstack-table/helpers/column-api'
import {isDragDropColumnId} from '../../hooks/tanstack-table/helpers/column-ids'
import {getVisibleCells, rowToggleSelected} from '../../hooks/tanstack-table/helpers/row-api'
import type {Cell, ColumnInstance, Row} from '../../hooks/tanstack-table/helpers/types'
import {
  useTableDeselectAllRows,
  useTableSelectAllRows,
  useTableSelectedFlatRows,
} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import {useTableSidePanel} from '../../hooks/use-side-panel'
import {isValueBulkEditableColumn} from '../../models/column-capabilities'
import type {MemexItemModel} from '../../models/memex-item-model'
import {handleKeyboardNavigation, suppressEvents} from '../../navigation/keyboard'
import {FocusType, NavigationDirection} from '../../navigation/types'
import {Direction} from '../../selection/types'
import {useClearColumnValue} from '../../state-providers/column-values/use-clear-column-value'
import {useSearch} from '../filter-bar/search-context'
import {useFillSelection} from './bulk-fill-values'
import {useCopyPaste} from './hooks/use-copy-paste'
import {
  cellHasFocus,
  focusCell,
  focusOmnibar,
  isCellFocus,
  isSuspended,
  moveTableFocus,
  useStableTableNavigation,
} from './navigation'
import {
  type SelectionMethods,
  useAreCellsBulkSelected,
  useBulkSelectedColumn,
  useGetBulkSelectedRows,
  useTableCellBulkSelectionActions,
} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'
import {useDeselectAll} from './use-deselect-all'
import {useRowSelection} from './use-row-selection'

/*
 *  NOTE: this shortcut is registered globally to access the search bar on GitHub
 *        and should be ignored within the application if the user tries to
 *        use it to edit a cell.
 */
const SHORTCUT_LEADER = '/'

export const FocusCellContext = createContext<{
  focusCellElement: () => void
  wrapperRef: React.RefObject<HTMLDivElement> | undefined
  childRef: React.RefObject<HTMLElement> | undefined
}>({
  focusCellElement: () => {},
  wrapperRef: undefined,
  childRef: undefined,
})

/**
 * Hook for configuring table focus state and behaviour for a given cell
 *
 * @param cell react-table cell associated with state and behaviour
 * @param editable flag to determine whether the cell can be editable (defaults to false if not provided)
 */
export const useTableFocusCell = (
  cell: Pick<Cell<TableDataType>, 'column' | 'row'>,
  isFocused: boolean,
  isEditing: boolean,
  isExpanded: boolean,
  editable = false,
  disabled = false,
  replaceContents = false,
  focusRef: React.RefObject<HTMLElement>,
) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLButtonElement>(null)
  const expandRef = useRef<HTMLButtonElement>(null)
  const {stateRef, navigationDispatch} = useStableTableNavigation()
  const readyToEdit = useRef(false)
  const {flatRows} = useTableInstance()
  const selectedRows = useTableSelectedFlatRows()
  const selectedRowsLength = selectedRows.length
  const {hasWritePermissions} = ViewerPrivileges()
  const {copyItems, paste} = useCopyPaste()
  const {expandRowSelectionInDirection} = useRowSelection(cell.row)
  const deselectAllRows = useTableDeselectAllRows()
  const selectAllRows = useTableSelectAllRows()
  const {openPane: openSidePanel} = useTableSidePanel()
  const {toggleFilter} = useSearch()
  const {hierarchyEnabled} = useTableHierarchy()

  const {
    bulkSelectActions,
    bulkSelectedColumn,
    canBulkEdit,
    clearColumnValue,
    deselectAll,
    fillSelection,
    hasBulkSelection,
    getBulkSelectedRows,
    openParentIssue,
  } = useBulkActionsForTableFocusCell(cell)
  const bulkSelectedColumnModel = bulkSelectedColumn ? getColumnModel(bulkSelectedColumn) : undefined
  const canBulkSelect = bulkSelectActions !== null

  const {nonNavigable, columnModel, typeToEditEnabled} = getColumnMeta(cell.column) ?? {}

  const focusIndex = useMemo(() => {
    return flatRows.findIndex(r => r.id === cell.row.id)
  }, [cell.row.id, flatRows])

  const refocusCell = useCallback(
    (removedItemIds: Array<number> = []) => {
      if (focusIndex === -1) return
      // Exclude rows that have been deleted or archived from the table
      const removedItemIdsSet = new Set(removedItemIds)
      const remainingRows = flatRows.filter(r => !removedItemIdsSet.has(r.original.id))
      const lastIndex = remainingRows.length - 1
      const nextFocusIndex = focusIndex > lastIndex ? lastIndex : focusIndex
      // Get the new row at the previously focused index
      const focusRow = remainingRows[nextFocusIndex]
      if (focusRow) {
        const nextCellFocus = getVisibleCells(focusRow)[1]
        if (!nextCellFocus) return
        navigationDispatch(focusCell(focusRow.id, nextCellFocus.column.id, false))
      } else {
        navigationDispatch(focusOmnibar())
      }
    },
    [flatRows, focusIndex, navigationDispatch],
  )

  const focusNextCell = useCallback(
    (removedItemIds: Array<number> = []) => {
      refocusCell(removedItemIds)
    },
    [refocusCell],
  )

  const onConfirmRemoval = useCallback(
    (removedItemIds: Array<number>) => {
      deselectAll()
      focusNextCell(removedItemIds)
    },
    [focusNextCell, deselectAll],
  )

  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation(undefined, onConfirmRemoval, refocusCell)
  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation(undefined, onConfirmRemoval, refocusCell)

  const onFocus = useCallback(() => {
    if (cellHasFocus(stateRef.current, cell.row.id, cell.column.id)) {
      // We don't want to dispatch the action if the cell is already focused in the navigation context.
      return
    }
    navigationDispatch(focusCell(cell.row.id, cell.column.id, false))
  }, [stateRef, cell.row.id, cell.column.id, navigationDispatch])

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (nonNavigable || e.isPropagationStopped() || e.shiftKey) {
        return
      }

      // Enter edit mode when the user clicks on the caret or when the user clicks on a focused cell
      const didClickDropdownCaret = e.target instanceof Node && dropdownRef.current?.contains(e.target)
      const didClickExpandButton = e.target instanceof Node && expandRef.current?.contains(e.target)
      const shouldEnterEditMode = hierarchyEnabled
        ? !didClickExpandButton && (readyToEdit.current || didClickDropdownCaret)
        : readyToEdit.current || didClickDropdownCaret

      if (editable && shouldEnterEditMode) {
        readyToEdit.current = false
        navigationDispatch(focusCell(cell.row.id, cell.column.id, editable))
        bulkSelectActions?.clearSelection()
      }
    },
    [nonNavigable, hierarchyEnabled, editable, navigationDispatch, cell.row.id, cell.column.id, bulkSelectActions],
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (nonNavigable) return

      if (canBulkSelect) {
        if (e.shiftKey) {
          bulkSelectActions.selectRange(cell, isPlatformMeta(e) ? 'add' : 'replace')
          e.preventDefault()
          return
        } else if (isPlatformMeta(e)) {
          bulkSelectActions.select(cell, 'toggle')
        } else {
          bulkSelectActions.clearSelection()
        }
      }

      if (!isFocused) {
        readyToEdit.current = false
      } else if (isFocused && editable) {
        readyToEdit.current = true
      }

      deselectAllRows()
    },
    [nonNavigable, canBulkSelect, isFocused, editable, deselectAllRows, bulkSelectActions, cell],
  )

  const openInNewTab = useCallback((url?: string) => {
    window.open(url, '_blank')
  }, [])

  const handleLinkedPullRequests = useCallback(
    (e: React.KeyboardEvent) => {
      const linkedPullRequests = cell.row.original.getLinkedPullRequests()
      // If there is only one linked pull request, open it in a new tab
      if (linkedPullRequests.length === 1) {
        openInNewTab(linkedPullRequests[0]?.url)
        // If there are multiple linked pull requests, open the dropdown
      } else if (linkedPullRequests.length > 1) {
        suppressEvents(e)
        focusRef?.current?.click()
      }
    },
    [cell.row.original, focusRef, openInNewTab],
  )

  const onKeyDown: React.KeyboardEventHandler = useCallback(
    e => {
      if (isDragDropColumnId(cell.column.id) || isEditing) {
        return
      }

      const focus = stateRef.current?.focus
      const suspended = focus && isCellFocus(focus) && isSuspended(focus)

      // If the cell is suspended, we don't want to handle any keyboard events
      // This is important for the linked pull requests dropdown, which is a suspended cell
      if (!isEditing && suspended && columnActionable(cell.row.original, columnModel?.dataType)) {
        return
      }

      const shortcut = shortcutFromEvent(e)

      if (canBulkSelect && (shortcut === SHORTCUTS.ARROW_LEFT || shortcut === SHORTCUTS.ARROW_RIGHT)) {
        bulkSelectActions.clearSelection()
      }

      const result = handleKeyboardNavigation(navigationDispatch, e)
      if (result.action) {
        deselectAllRows() // keep cell selection to allow for selecting multiple separate ranges
        suppressEvents(e)
      } else {
        switch (shortcut) {
          case SHORTCUTS.ENTER: {
            if (editable) {
              navigationDispatch(moveTableFocus({focusType: FocusType.Edit}))
              suppressEvents(e)
            } else if (columnModel?.dataType === MemexColumnDataType.LinkedPullRequests) {
              handleLinkedPullRequests(e)
            }
            return
          }
          case SHORTCUTS.HOME: {
            navigationDispatch(moveTableFocus({x: NavigationDirection.First, focusType: FocusType.Focus}))
            deselectAllRows()
            suppressEvents(e)
            return
          }
          // WCAG standards say this should select the column, but it conflicts (on Windows) with our command+space shortcut to focus the omnibar
          // case SHORTCUTS.META_SPACE: {
          //   if (canUseBulkActions) {
          //     bulkSelectActions.selectColumn(cell.column.id, cell.row.index)
          //     suppressEvents(e)
          //   }
          //   return
          // }
          case SHORTCUTS.SHIFT_SPACE: {
            if (hasWritePermissions) {
              suppressEvents(e)
              rowToggleSelected(cell.row, true)
            }
            return
          }
          case SHORTCUTS.SPACE: {
            if (cell.column.id === 'Title') {
              openSidePanel(cell.row.original)
              suppressEvents(e)
            }
            if (columnModel?.dataType === MemexColumnDataType.ParentIssue) {
              openParentIssue(cell.row.original.getParentIssue())
              suppressEvents(e)
            }
            if (columnModel?.dataType === MemexColumnDataType.SubIssuesProgress) {
              toggleFilter('parent-issue', cell.row.original.getNameWithOwnerReference())
              suppressEvents(e)
            }
            if (columnModel?.dataType === MemexColumnDataType.Repository) {
              const repositoryUrl = cell.row.original.getExtendedRepository()?.url

              if (repositoryUrl) {
                openInNewTab(repositoryUrl)
                suppressEvents(e)
              }
            }
            if (columnModel?.dataType === MemexColumnDataType.LinkedPullRequests) {
              handleLinkedPullRequests(e)
            }
            return
          }
          case SHORTCUTS.META_ENTER: {
            if (canBulkSelect) {
              bulkSelectActions.select(cell, 'toggle')
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.SHIFT_ENTER: {
            if (canBulkSelect) {
              bulkSelectActions.selectRange(cell, 'replace')
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.META_SHIFT_ENTER: {
            if (canBulkSelect) {
              bulkSelectActions.selectRange(cell, 'add')
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.SHIFT_ARROW_UP: {
            if (selectedRowsLength > 0) {
              expandRowSelectionInDirection(Direction.Up, cell.row)
              suppressEvents(e)
            } else if (canBulkSelect) {
              bulkSelectActions.expandSelectionUp(cell)
              navigationDispatch(moveTableFocus({y: NavigationDirection.Previous, focusType: FocusType.Focus}))
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.SHIFT_ARROW_DOWN: {
            if (selectedRowsLength > 0) {
              expandRowSelectionInDirection(Direction.Down, cell.row)
              suppressEvents(e)
            } else if (canBulkSelect) {
              bulkSelectActions.expandSelectionDown(cell)
              navigationDispatch(moveTableFocus({y: NavigationDirection.Next, focusType: FocusType.Focus}))
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.END: {
            navigationDispatch(moveTableFocus({x: NavigationDirection.Last, focusType: FocusType.Focus}))
            deselectAllRows()
            suppressEvents(e)
            return
          }
          case SHORTCUTS.META_C: {
            if (cell.row.original.contentType !== ItemType.RedactedItem) {
              if (selectedRows.length > 0) {
                copyItems(
                  selectedRows.map(row => row.original),
                  // only include headers when copying whole table; this makes the result closer to what the user sees
                  // important that we use selectedFlatRows and not the filtered selectedRows here to include redacted
                  // items in the count
                  {withHeaders: selectedRowsLength === flatRows.length},
                )
              } else {
                const bulkSelectedRows = getBulkSelectedRows().map(row => row.original)
                if (bulkSelectedRows.length && bulkSelectedColumnModel) {
                  copyItems(bulkSelectedRows, {selectedFields: [bulkSelectedColumnModel]})
                } else if (columnModel) {
                  copyItems([cell.row.original], {selectedFields: [columnModel]})
                }
              }
              e.preventDefault()
            }
            return
          }
          case SHORTCUTS.META_V: {
            if (hasWritePermissions && cell.row.original.contentType !== ItemType.RedactedItem) {
              paste(
                cell,
                getBulkSelectedRows()
                  .sort((a, b) => a.index - b.index)
                  .map(row => row.original),
              )
              e.preventDefault()
            }

            return
          }
          case SHORTCUTS.META_A: {
            if (selectedRowsLength === flatRows.length) {
              deselectAllRows()
            } else if (selectedRowsLength > 0) {
              selectAllRows()
            } else if (selectedRowsLength === 0) {
              rowToggleSelected(cell.row, true)
            }
            suppressEvents(e)
            return
          }
          case SHORTCUTS.DELETE:
          case SHORTCUTS.BACKSPACE: {
            if (hasWritePermissions && selectedRows.length > 0) {
              suppressEvents(e)
              const selectedRowsAsNumbers = selectedRows.map(r => r.original.id)
              openRemoveConfirmationDialog(selectedRowsAsNumbers, TableRowKeyboardUI)
            } else if (editable) {
              const rows = getBulkSelectedRows()
              if (rows.length === 0) rows.push(cell.row)

              clearColumnValue(getColumnModel(cell.column), rows)
              suppressEvents(e)
            }
            return
          }
          case SHORTCUTS.META_D: {
            if (canBulkEdit && hasWritePermissions && hasBulkSelection) {
              suppressEvents(e)
              fillSelection('down')
            }
            return
          }
          case SHORTCUTS.ARCHIVE: {
            if (
              selectedRows.length &&
              hasWritePermissions &&
              selectedRows.every(row => isMemexItemTypeArchivable(row.original.contentType))
            ) {
              suppressEvents(e)
              const selectedRowsAsNumbers = selectedRows.map(r => r.original.id)
              openArchiveConfirmationDialog(selectedRowsAsNumbers, TableRowKeyboardUI)
              return
            }
          }
        }

        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key.match(/^\S$/) && !anyModifierPressed(e) && e.key !== SHORTCUT_LEADER) {
          // for any single non-white space character, switch to edit mode
          // ignore if any of ALT, CTRL and Command/Windows key pressed

          const shouldTransitionToEditModeOnKeydown = typeToEditEnabled !== false
          if (editable && shouldTransitionToEditModeOnKeydown) {
            // signal to the cell editor that it needs to replace the
            // default text of the input with the text that was just
            // entered by the user when it is first rendered
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            navigationDispatch(focusCell(cell.row.id, cell.column.id, true, false, true, e.key))
          }

          suppressEvents(e)
          return
        }
      }
    },
    [
      cell,
      isEditing,
      stateRef,
      columnModel,
      canBulkSelect,
      navigationDispatch,
      bulkSelectActions,
      deselectAllRows,
      editable,
      handleLinkedPullRequests,
      hasWritePermissions,
      openSidePanel,
      openParentIssue,
      toggleFilter,
      openInNewTab,
      selectedRowsLength,
      expandRowSelectionInDirection,
      selectedRows,
      copyItems,
      flatRows.length,
      getBulkSelectedRows,
      bulkSelectedColumnModel,
      paste,
      selectAllRows,
      openRemoveConfirmationDialog,
      clearColumnValue,
      canBulkEdit,
      hasBulkSelection,
      fillSelection,
      openArchiveConfirmationDialog,
      typeToEditEnabled,
    ],
  )

  /** Drag to select multiple cells. */
  const onMouseEnter: React.MouseEventHandler = useCallback(
    event => {
      if (event.buttons !== 1 || !bulkSelectActions || nonNavigable) return

      const focus = stateRef.current?.focus

      // Rather than tracking a global 'dragging' state, we just check the current state of the table to see whether
      // the drag started in a valid spot. Dragging is only valid within a column and cells are selected on mousedown,
      // so we only need to make sure the selection is in this column. Otherwise we ignore it to avoid clearing the
      // user's selection if they accidentally left the column while dragging.
      const focusedColumnId = focus && isCellFocus(focus) && !isSuspended(focus) ? focus.details.x : null
      if (cell.column.id !== focusedColumnId) return

      bulkSelectActions.selectRange(cell, isPlatformMeta(event) ? 'add' : 'replace')
    },
    [bulkSelectActions, cell, nonNavigable, stateRef],
  )
  const wrapperProps = useMemo(
    () => ({
      ref: wrapperRef,
      onKeyDown,
      onClick,
      onMouseDown,
      onFocus,
      onMouseEnter,
      tabIndex: nonNavigable ? undefined : 0,
    }),
    [onKeyDown, onClick, onMouseDown, onFocus, onMouseEnter, nonNavigable],
  )

  const contentsProps: ContentsProps = useMemo(() => {
    if (isEditing) {
      return {replaceContents, isDisabled: disabled, isEditing, isExpanded}
    } else {
      return {dropdownRef, expandRef, isDisabled: disabled, isEditing, isExpanded}
    }
  }, [isEditing, isExpanded, dropdownRef, replaceContents, disabled])

  return {
    wrapperProps,
    contentsProps,
  }
}
export type EditorContentProps = {
  replaceContents: boolean
  isDisabled: boolean
  dropdownRef?: undefined
  isEditing: true
  isExpanded: boolean
}
export type CellContentProps = {
  dropdownRef: RefObject<HTMLButtonElement>
  expandRef: RefObject<HTMLButtonElement>
  isDisabled: boolean
  replaceContents?: undefined
  isEditing: false
  isExpanded: boolean
}
export type ContentsProps = EditorContentProps | CellContentProps

/**
 * Check if any keyboard modifier (ALT, CTRL or Command/Windows key) was pressed
 * when this keyboard event was raised.
 */
function anyModifierPressed(e: React.KeyboardEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return e.altKey || e.ctrlKey || e.metaKey
}

function useBulkActionsForTableFocusCell(cell: {row: Row<MemexItemModel>; column: ColumnInstance<MemexItemModel>}) {
  const {memex_mwl_table_cell_perf} = useEnabledFeatures()
  if (memex_mwl_table_cell_perf) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBulkActionsForTableFocusCellWithPerfEnabled()
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBulkActionsForTableFocusCellWithPerfDisabled(cell)
  }
}

function useBulkActionsForTableFocusCellWithPerfDisabled(cell: {
  row: Row<MemexItemModel>
  column: ColumnInstance<MemexItemModel>
}) {
  const columnModel = getColumnModel(cell.column)
  const bulkSelectActions = useTableCellBulkSelectionActions()
  const canBulkSelect = bulkSelectActions !== null
  const canBulkEdit = canBulkSelect && columnModel && isValueBulkEditableColumn(columnModel.dataType)
  const hasBulkSelection = useAreCellsBulkSelected()
  const bulkSelectedColumn = useBulkSelectedColumn()
  const getBulkSelectedRows = useGetBulkSelectedRows()

  const fillSelection = useFillSelection()

  const {clearColumnValue} = useClearColumnValue()
  const deselectAll = useDeselectAll()

  const {openParentIssue} = useOpenParentIssue()

  return {
    bulkSelectActions,
    canBulkEdit,
    hasBulkSelection,
    bulkSelectedColumn,
    fillSelection,
    clearColumnValue,
    deselectAll,
    getBulkSelectedRows,
    openParentIssue,
  }
}

const getBulkSelectedRowsNoop = () => {
  return []
}

const fillSelectionNoop = async () => {}

const clearColumnValueNoop = async () => {}
const deselectAllNoop = () => {}

function useBulkActionsForTableFocusCellWithPerfEnabled() {
  const bulkSelectActions: SelectionMethods | null = null
  const canBulkEdit = false
  const hasBulkSelection = false
  const bulkSelectedColumn = null

  const {openParentIssue} = useOpenParentIssue()

  return {
    bulkSelectActions,
    canBulkEdit,
    hasBulkSelection,
    bulkSelectedColumn,
    fillSelection: fillSelectionNoop,
    clearColumnValue: clearColumnValueNoop,
    deselectAll: deselectAllNoop,
    getBulkSelectedRows: getBulkSelectedRowsNoop,
    openParentIssue,
  }
}
